import React, { Component, ErrorInfo } from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'An unexpected error occurred';
      const errorStack = this.state.error?.stack;

      return (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              width: 'min(900px, 90vw)',
              maxHeight: '90vh',
              overflow: 'auto',
              background: '#ffffff',
              color: '#111827',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
              padding: 24,
              fontFamily: 'Montserrat, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Arial, sans-serif',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div
                aria-hidden
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '9999px',
                  background: 'rgba(220,38,38,0.15)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <span style={{ color: '#dc2626', fontWeight: 700 }}>!</span>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#dc2626' }}>
                  Something went wrong
                </h3>
                <p style={{ margin: 0, marginTop: 4, color: '#111827' }}>{errorMessage}</p>
                <p style={{ margin: 0, marginTop: 4, color: '#6b7280', fontSize: 12 }}>
                  Location: ErrorBoundary
                </p>
              </div>
            </div>

            {errorStack && (
              <details open style={{ marginTop: 16 }}>
                <summary style={{ cursor: 'pointer', color: '#6b7280' }}>Technical Details</summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontSize: 12,
                    background: '#f8f8f8',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 12,
                    marginTop: 8,
                    color: '#111827',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  {errorStack}
                </pre>
              </details>
            )}

            <div style={{ marginTop: 16 }}>
              <h4 style={{ margin: 0, marginBottom: 8, fontSize: 14 }}>Error Report</h4>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  fontSize: 12,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: 12,
                  color: '#111827',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                {`Error Details:

Message: ${errorMessage}
Component: ErrorBoundary
Stack Trace: ${errorStack || 'Not available'}

Additional Information:
URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}
User Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}
Timestamp: ${new Date().toISOString()}`}
              </pre>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => {
                  const subject = encodeURIComponent('Error Report: ErrorBoundary');
                  const body = encodeURIComponent(
                    `Error Details:\n\nMessage: ${errorMessage}\nComponent: ErrorBoundary\nStack Trace: ${errorStack || 'Not available'}\n\nAdditional Information:\nURL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}\nUser Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}\nTimestamp: ${new Date().toISOString()}`
                  );
                  if (typeof window !== 'undefined') {
                    window.location.href = `mailto:superman50196@gmail.com?subject=${subject}&body=${body}`;
                  }
                }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  background: '#eff6ff',
                  color: '#1d4ed8',
                  cursor: 'pointer',
                }}
              >
                Email
              </button>
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  background: '#f3f4f6',
                  color: '#111827',
                  cursor: 'pointer',
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 